/**
 * Template variable registry.
 *
 * Variables let template authors embed dynamic, Medplum-backed values inside
 * any text-bearing field of a `Questionnaire` (form title, section header,
 * question text, helper / description, etc.). Each variable is identified by
 * a stable token (e.g. `{{organization.name}}`) and resolves at render time
 * against a {@link VariableContext}.
 *
 * Design goals:
 *   - **Open registry**: feature areas can add definitions without touching
 *     the resolver. Definitions describe metadata only — the resolver pulls
 *     the value from `VariableContext`.
 *   - **Safe by default**: unknown / unresolved tokens render either as the
 *     literal token (admin-facing surfaces) or as a friendly placeholder
 *     (client-facing surfaces). The behavior is selectable per call.
 *   - **Side-effect free**: registry definitions are pure data; no Medplum
 *     calls happen here. Loading happens in `useVariableContext` and is
 *     passed in to the resolver.
 *
 * The chosen syntax is `{{namespace.field}}` — same as Mustache / many
 * established template engines so it's instantly familiar to authors and
 * easy to escape if a literal `{{` is ever needed (`\{{`).
 */

import type { ReactNode } from 'react';

/**
 * Loaded values that {@link resolveVariableValue} can read from. Each section
 * is optional so callers can populate progressively (e.g. preview mode might
 * have org but not patient context yet).
 */
export interface VariableContext {
  organization?: {
    /** Primary organization name shown to clients (e.g. "Joyful Therapy Group"). */
    name?: string;
    /** Primary public phone number, formatted for display. */
    phone?: string;
    /** Primary public email address. */
    email?: string;
    /** Primary website URL. */
    website?: string;
  };
  /** Supervising clinician — populated for pre-licensed providers. */
  supervisor?: {
    name?: string;
    fullName?: string;
    licenseNumber?: string;
    npiNumber?: string;
  };
  /**
   * Raw bag for variables introduced ad-hoc by feature teams. Keys here can
   * be referenced via `{{custom.<key>}}` in templates.
   */
  custom?: Record<string, string>;
}

export interface VariableDefinition {
  /**
   * Stable identifier — also the token authors type. Always
   * `namespace.field`; namespaces map 1:1 to keys on {@link VariableContext}.
   */
  id: string;
  /** Short human label shown in the variable picker. */
  label: string;
  /** One-line description shown beneath the label in the picker. */
  description: string;
  /** Optional icon for the picker UI. */
  icon?: ReactNode;
  /** Pure resolver — given a context, returns the substitution text. */
  resolve: (ctx: VariableContext) => string | undefined;
}

/**
 * Default registry: organization-level fields. Feature teams can call
 * {@link registerVariables} to extend the registry on app boot without
 * touching this file.
 */
const baseDefinitions: VariableDefinition[] = [
  {
    id: 'organization.name',
    label: 'Organization name',
    description: 'Public name of the practice sending the form.',
    resolve: (ctx) => ctx.organization?.name,
  },
  {
    id: 'organization.phone',
    label: 'Organization phone',
    description: 'Primary phone number for the practice.',
    resolve: (ctx) => ctx.organization?.phone,
  },
  {
    id: 'organization.email',
    label: 'Organization email',
    description: 'Primary contact email for the practice.',
    resolve: (ctx) => ctx.organization?.email,
  },
  {
    id: 'organization.website',
    label: 'Organization website',
    description: 'Public website for the practice.',
    resolve: (ctx) => ctx.organization?.website,
  },
  {
    id: 'supervisor.name',
    label: 'Supervisor name',
    description: 'Display name of the supervising clinician.',
    resolve: (ctx) => ctx.supervisor?.name,
  },
  {
    id: 'supervisor.fullName',
    label: 'Supervisor full name',
    description: 'Full legal name of the supervising clinician.',
    resolve: (ctx) => ctx.supervisor?.fullName,
  },
  {
    id: 'supervisor.licenseNumber',
    label: 'Supervisor license number',
    description: 'Professional license number of the supervising clinician.',
    resolve: (ctx) => ctx.supervisor?.licenseNumber,
  },
  {
    id: 'supervisor.npiNumber',
    label: 'Supervisor NPI number',
    description: 'NPI number of the supervising clinician.',
    resolve: (ctx) => ctx.supervisor?.npiNumber,
  },
];

const registry: Map<string, VariableDefinition> = new Map(
  baseDefinitions.map((d) => [d.id, d]),
);

/** Append (or replace) variable definitions. Stable across the app lifetime. */
export function registerVariables(definitions: VariableDefinition[]): void {
  for (const def of definitions) {
    registry.set(def.id, def);
  }
}

/** Snapshot of the current registry, suitable for picker UIs. */
export function getRegisteredVariables(): VariableDefinition[] {
  return [...registry.values()];
}

export function getVariableDefinition(id: string): VariableDefinition | undefined {
  return registry.get(id);
}

/**
 * The token syntax: `{{namespace.field}}`. Whitespace around the identifier is
 * tolerated so authors typing `{{ organization.name }}` still get a valid
 * substitution.
 */
export const VARIABLE_TOKEN_REGEX = /\{\{\s*([a-zA-Z][\w-]*(?:\.[a-zA-Z][\w-]*)+)\s*\}\}/g;

export function buildVariableToken(id: string): string {
  return `{{${id}}}`;
}
